import { FC, useState } from 'react';
import { RiAlarmWarningFill } from 'react-icons/ri';
import { Button } from '../../../../components/shared/button/Button';
import { TSCard } from '../../../../components/shared/card/Card';
import { Icon } from '../../../../components/shared/icons/Icon';
import { ConfirmDialog } from '../../../../components/shared/modal/ConfirmDialog';
import { Title } from '../../../../components/shared/text/Title';
import { handleMutationResult } from '../../../../helpers/toastHelper';
import {
  useGdprDeleteUserMutation,
  useUpdateDeactivatedStatusMutation
} from '../../../../services/gql/graphql.generated';
import strings from '../../team.translations';
import { TeamMemberUpdateType, useTeamMemberService } from '../TeamMemberService';

interface DangerCardProps {
  employeeId: string;
  isDeactivated: boolean;
  firstName: string;
  lastName: string;
  disableDeactivate?: boolean;
  disableDelete?: boolean;
}

export const DangerCard: FC<DangerCardProps> = ({
  employeeId,
  isDeactivated,
  firstName,
  lastName,
  disableDeactivate,
  disableDelete
}) => {
  const forceUpdate = useTeamMemberService(state => state.forceUpdate);

  const [deleteAlertOpen, setDeleteAlertOpen] = useState<boolean>(false);

  const [deactivateAlertOpen, setDeactivateAlertOpen] = useState<boolean>(false);

  const [deactivatedRequest, updateDeactivated] = useUpdateDeactivatedStatusMutation();

  const deactivatedStatus = isDeactivated;

  const [deleteRequest, gdprDeleteUser] = useGdprDeleteUserMutation();

  const deleteUser = async () => {
    if (!deleteRequest.fetching) {
      const result = await gdprDeleteUser({
        id: employeeId
      });

      await handleMutationResult(result, {
        successMessage: 'User deleted successfully.',
        errorMessage: 'Unable to delete user.',
        onSuccess: () => forceUpdate(TeamMemberUpdateType.ALL)
      });
    }
  };

  const toggleDeactivate = async () => {
    if (!deactivatedRequest.fetching) {
      const result = await updateDeactivated({
        id: employeeId,
        deactivated: !deactivatedStatus
      });

      await handleMutationResult(result, {
        successMessage: 'User successfully deactivated.',
        errorMessage: 'Unable to deactivate user.',
        onSuccess: () => forceUpdate(TeamMemberUpdateType.ALL)
      });
    }
  };

  return (
    <TSCard className="flex flex-col flex-1 p-6 gap-4 !border-red-600">
      <Title className="flex items-center space-x-2 border-solid border-b pb-4">
        <Icon theme="red" Icon={RiAlarmWarningFill} />
        <div className="space-x-2 flex items-center justify-center">
          <span className="whitespace-nowrap text-red-600">{strings.dangerCard.cardTitle}</span>
        </div>
      </Title>
      <div className="flex flex-row w-full items-center flex-wrap md:flex-nowrap -mt-4">
        {!disableDeactivate && (
          <div className="flex mr-0 md:mr-8 items-center mt-[10px]">
            <div className="w-full">
              <p className="font-semibold">
                {deactivatedStatus ? strings.dangerCard.reactivateAccount : strings.dangerCard.deactivateAccount}
              </p>
              <p className="pr-3">
                {deactivatedStatus
                  ? strings.dangerCard.reactivateDescription
                  : strings.dangerCard.deactivateDescription}
              </p>
            </div>
            <Button
              onClick={() => {
                if (!disableDeactivate) setDeactivateAlertOpen(true);
              }}
              disabled={disableDeactivate}
              loading={deactivatedRequest.fetching}
              className="h-full flex-shrink-0"
              danger={!deactivatedStatus}
              tint={!deactivatedStatus}
            >
              {deactivatedStatus ? strings.dangerCard.reactivate : strings.dangerCard.deactivate}
            </Button>
          </div>
        )}
        {!disableDeactivate && (
          <div className="flex items-center mt-[10px]">
            <div>
              <p className="font-semibold">{strings.dangerCard.deleteAccount}</p>
              <p className="pr-3">{strings.dangerCard.deleteDescription}</p>
            </div>
            <Button
              onClick={() => {
                if (!disableDelete) setDeleteAlertOpen(true);
              }}
              disabled={disableDelete}
              loading={deleteRequest.fetching}
              className="h-full flex-shrink-0"
              danger
            >
              {strings.common.delete}
            </Button>
          </div>
        )}
      </div>

      <div className="w-full flex justify-center items-center">
        <ConfirmDialog
          title={deactivatedStatus ? strings.dangerCard.reactivateAccount : strings.dangerCard.deactivateAccount}
          open={deactivateAlertOpen}
          onClose={() => setDeactivateAlertOpen(false)}
          onConfirm={() => {
            if (!disableDeactivate) {
              setDeactivateAlertOpen(false);
              toggleDeactivate();
            }
          }}
          confirmLabel={deactivatedStatus ? strings.dangerCard.reactivate : strings.dangerCard.deactivate}
          cancelLabel={strings.common.cancel}
          dangerous
        >
          <div className="text-secondary">
            <p className="mb-4">
              {deactivatedStatus
                ? strings.dangerCard.reactivateModalConfirmation
                : strings.dangerCard.deactivateModalConfirmation}
            </p>

            {!deactivatedStatus && (
              <>
                <p>{strings.dangerCard.thisWill}</p>
                <p className="font-semibold">
                  {strings.dangerCard.willDescriptionPartOne.replace('{name}', `${firstName} ${lastName}`)}
                </p>
                <p className="mb-4 font-semibold">
                  {strings.dangerCard.willDescriptionPartTwo.replace('{name}', `${firstName} ${lastName}`)}
                </p>

                <p>{strings.dangerCard.thisWont}</p>
                <p className="font-semibold">
                  {strings.dangerCard.wontDescriptionPartOne.replace('{name}', `${firstName} ${lastName}`)}
                </p>
                <p className="mb-4 font-semibold">
                  {strings.dangerCard.wontDescriptionPartTwo.replace('{name}', `${firstName} ${lastName}`)}
                </p>

                <p>{strings.dangerCard.stillCharged}</p>
                <p>{strings.dangerCard.stillReactivate}</p>
              </>
            )}
          </div>
        </ConfirmDialog>
        <ConfirmDialog
          title={strings.dangerCard.deleteAccount}
          open={deleteAlertOpen}
          onClose={() => setDeleteAlertOpen(false)}
          onConfirm={() => {
            if (!disableDelete) {
              setDeleteAlertOpen(false);
              deleteUser();
            }
          }}
          confirmLabel={strings.common.delete}
          cancelLabel={strings.common.cancel}
          dangerous
        >
          <div className="text-secondary mb-4">
            <p className="mb-4">{strings.dangerCard.deactivateModalConfirmation}</p>

            <p>{strings.dangerCard.thisWill}</p>
            <p className="font-semibold">
              {strings.dangerCard.willDescriptionPartOne.replace('{name}', `${firstName} ${lastName}`)}
            </p>
            <p className="font-semibold">
              {strings.dangerCard.willDescriptionPartTwo.replace('{name}', `${firstName} ${lastName}`)}
            </p>
            <p className="mb-4 font-semibold">
              {strings.dangerCard.willDescriptionPartThree.replace('{name}', `${firstName} ${lastName}`)}
            </p>

            <p>{strings.dangerCard.thisWont}</p>
            <p className="mb-4 font-semibold">
              {strings.dangerCard.wontDescriptionPartTwo.replace('{name}', `${firstName} ${lastName}`)}
            </p>

            <p>{strings.dangerCard.stopCharged}</p>
          </div>
          <p className="bg-red-200 text-red-600 mx-[-1.75rem] p-2">{strings.dangerCard.cantRecover}</p>
        </ConfirmDialog>
      </div>
    </TSCard>
  );
};
